import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  serviceInterest: "Four Lenses Classes",
  message: "",
  preferredContact: "Email",
  website: "", // honeypot
};

const services = [
  "Four Lenses Classes",
  "Custom Leadership Consulting",
  "Not sure yet",
];

const fieldCls =
  "w-full bg-background border border-white/15 px-4 py-3.5 text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors duration-200 font-sans";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/50 mb-2 block";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // double-submit guard
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Message received. We'll be in touch shortly.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many submissions. Please try again in a little while.");
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 sm:py-40 border-t border-white/10" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 lg:gap-24">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">/ Start Your Journey</span>
          <h2 className="mt-6 font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
            Ready to <br /> transform your <span className="text-primary">leadership?</span>
          </h2>
          <p className="mt-8 max-w-md text-foreground/60 leading-relaxed">
            Tell us about your team and the challenges you're navigating. We'll get back to you to
            discuss how FuzePoint can help you achieve breakthrough results.
          </p>
          <div className="mt-12 space-y-4 font-mono text-sm text-foreground/50">
            <p className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-primary rotate-45" /> Response within 1 business day
            </p>
            <p className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-primary rotate-45" /> No obligation consultation
            </p>
          </div>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="border border-primary/40 bg-card p-12 flex flex-col items-start justify-center"
            data-testid="contact-success"
          >
            <span className="h-3 w-3 bg-primary rotate-45 mb-6" />
            <h3 className="font-display font-bold uppercase text-3xl tracking-tight">Message received.</h3>
            <p className="mt-4 text-foreground/60 max-w-sm leading-relaxed">
              Thank you for reaching out. A member of the FuzePoint team will be in touch shortly.
            </p>
            <button
              onClick={() => setDone(false)}
              data-testid="contact-reset"
              className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-primary hover:text-white transition-colors"
            >
              Send another message →
            </button>
          </motion.div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
            {/* honeypot — visually hidden, off-screen */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={set("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
              data-testid="hp-website"
            />

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>First Name *</label>
                <input required value={form.firstName} onChange={set("firstName")} className={fieldCls} placeholder="Jane" data-testid="input-firstName" />
              </div>
              <div>
                <label className={labelCls}>Last Name *</label>
                <input required value={form.lastName} onChange={set("lastName")} className={fieldCls} placeholder="Doe" data-testid="input-lastName" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Email *</label>
                <input required type="email" value={form.email} onChange={set("email")} className={fieldCls} placeholder="jane@company.com" data-testid="input-email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="+1 (555) 000-0000" data-testid="input-phone" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Company</label>
                <input value={form.company} onChange={set("company")} className={fieldCls} placeholder="Acme Inc." data-testid="input-company" />
              </div>
              <div>
                <label className={labelCls}>Role</label>
                <input value={form.role} onChange={set("role")} className={fieldCls} placeholder="Director of Ops" data-testid="input-role" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Service Interest</label>
                <select value={form.serviceInterest} onChange={set("serviceInterest")} className={fieldCls} data-testid="input-serviceInterest">
                  {services.map((s) => (
                    <option key={s} value={s} className="bg-background">{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Preferred Contact</label>
                <select value={form.preferredContact} onChange={set("preferredContact")} className={fieldCls} data-testid="input-preferredContact">
                  <option value="Email" className="bg-background">Email</option>
                  <option value="Phone" className="bg-background">Phone</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Message *</label>
              <textarea required rows={5} value={form.message} onChange={set("message")} className={`${fieldCls} resize-none`} placeholder="Tell us about your team and what you're working toward…" data-testid="input-message" />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-primary px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-white hover:text-background transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
